import type { AssessmentPatientContext, AssessmentProfessionalContext } from '../types';
import { PERIPHERAL_MUSCLE_STRENGTH_DEFINITIONS } from '../peripheral-muscle/peripheralMuscleDomain';

export const ONE_RM_MUSCLE_GROUP_OPTIONS = PERIPHERAL_MUSCLE_STRENGTH_DEFINITIONS.map(
  (definition) => definition.shortLabel,
) as readonly string[];

export const ONE_RM_OTHER_OPTION = 'Outro';

export const ONE_RM_PROTOCOL_STEPS = [
  'Selecionar o grupo muscular a ser testado e definir o exercício correspondente, mantendo técnica padronizada e sem compensações.',
  'Para 1RM direto, progredir a carga com descansos adequados até identificar a maior carga completada em uma repetição válida.',
  'Para estimativa por carga submáxima, utilizar uma carga que permita no máximo 10 repetições válidas.',
  'Registrar a carga submáxima utilizada e o número de repetições efetivamente completadas até a falha concêntrica ou limite técnico.',
  'O sistema calcula o 1RM estimado pela média das equações de Mayhew et al. (1992) e Wathen (1994), exibindo também cada valor separadamente.',
] as const;

export const ONE_RM_REFERENCE_NOTES = [
  'Mayhew et al. (1992): 1RM = carga x (100 / (52,2 + 41,9 x e^(-0,055 x repetições)))',
  'Wathen (1994): 1RM = carga x (100 / (48,8 + 53,8 x e^(-0,075 x repetições)))',
  'As equações foram validadas com cargas submáximas que permitiram, no máximo, 10 repetições.',
] as const;

export const ONE_RM_STUDY_REFERENCES = [
  'Mayhew JL, Ball TE, Arnold MD, Bowen JC. Relative muscular endurance performance as a predictor of bench press strength in college men and women. Journal of Applied Sport Science Research. 1992;6:200-206.',
  'Wathen D. Load assignment. In: Baechle TR, editor. Essentials of Strength Training and Conditioning. Champaign, IL: Human Kinetics; 1994. p. 435-446.',
] as const;

export interface OneRMEntry {
  id: string;
  muscleGroup: string;
  direct1RMKg: number | null;
  submaxLoadKg: number | null;
  repetitions: number | null;
  estimated1RMKg: number | null;
  estimated1RMMayhewKg: number | null;
  estimated1RMWathenKg: number | null;
  notes?: string;
}

export interface OneRMResultPayload {
  entries: OneRMEntry[];
  notes?: string;
  patientContext?: AssessmentPatientContext;
  professionalContext?: AssessmentProfessionalContext;
  estimationMethod?: 'mean_mayhew_wathen';
  finishedAt?: string;
  protocolSteps?: string[];
  referenceNotes?: string[];
}

export interface OneRMSummary {
  groupCount: number;
  directCount: number;
  estimatedCount: number;
  bothMethodsCount: number;
  bestDirect1RMKg: number | null;
  bestEstimated1RMKg: number | null;
  bestOverallKg: number | null;
  bestGroupLabel: string | null;
}

const toPositiveNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number.parseFloat(String(value).replace(',', '.'));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const toValidRepetitions = (value: unknown): number | null => {
  const parsed = toPositiveNumber(value);
  if (parsed == null) return null;
  const rounded = Math.round(parsed);
  return rounded >= 1 && rounded <= 10 ? rounded : null;
};

const sanitizeText = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

export const calculateMayhew1RM = (loadKg: number | null, repetitions: number | null): number | null => {
  if (loadKg == null || repetitions == null || repetitions < 1 || repetitions > 10) return null;
  return loadKg * (100 / (52.2 + 41.9 * Math.exp(-0.055 * repetitions)));
};

export const calculateWathen1RM = (loadKg: number | null, repetitions: number | null): number | null => {
  if (loadKg == null || repetitions == null || repetitions < 1 || repetitions > 10) return null;
  return loadKg * (100 / (48.8 + 53.8 * Math.exp(-0.075 * repetitions)));
};

export const calculateMeanEstimated1RM = (loadKg: number | null, repetitions: number | null): number | null => {
  const mayhew = calculateMayhew1RM(loadKg, repetitions);
  const wathen = calculateWathen1RM(loadKg, repetitions);
  if (mayhew == null && wathen == null) return null;
  if (mayhew == null) return wathen;
  if (wathen == null) return mayhew;
  return (mayhew + wathen) / 2;
};

export const normalizeOneRMEntry = (value: unknown): OneRMEntry | null => {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const muscleGroup = sanitizeText(record.muscleGroup);
  const direct1RMKg = toPositiveNumber(record.direct1RMKg);
  const submaxLoadKg = toPositiveNumber(record.submaxLoadKg);
  const repetitions = toValidRepetitions(record.repetitions);
  const estimated1RMMayhewKg = calculateMayhew1RM(submaxLoadKg, repetitions);
  const estimated1RMWathenKg = calculateWathen1RM(submaxLoadKg, repetitions);
  const estimated1RMKg = calculateMeanEstimated1RM(submaxLoadKg, repetitions);

  if (!muscleGroup || (direct1RMKg == null && estimated1RMKg == null)) {
    return null;
  }

  return {
    id: sanitizeText(record.id) || `one-rm-${Math.random().toString(36).slice(2, 10)}`,
    muscleGroup,
    direct1RMKg,
    submaxLoadKg,
    repetitions,
    estimated1RMKg,
    estimated1RMMayhewKg,
    estimated1RMWathenKg,
    notes: sanitizeText(record.notes) || undefined,
  };
};

export const normalizeOneRMPayload = (value: unknown): OneRMResultPayload | null => {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const entriesSource = Array.isArray(record.entries) ? record.entries : [];
  const entries = entriesSource.map(normalizeOneRMEntry).filter((entry): entry is OneRMEntry => entry != null);
  if (!entries.length) return null;

  return {
    entries,
    notes: sanitizeText(record.notes) || undefined,
    patientContext:
      record.patientContext && typeof record.patientContext === 'object'
        ? (record.patientContext as AssessmentPatientContext)
        : undefined,
    professionalContext:
      record.professionalContext && typeof record.professionalContext === 'object'
        ? (record.professionalContext as AssessmentProfessionalContext)
        : undefined,
    estimationMethod: 'mean_mayhew_wathen',
    finishedAt: sanitizeText(record.finishedAt) || undefined,
    protocolSteps: ONE_RM_PROTOCOL_STEPS.slice(),
    referenceNotes: ONE_RM_REFERENCE_NOTES.slice(),
  };
};

export const summarizeOneRMPayload = (payload: OneRMResultPayload | null | undefined): OneRMSummary => {
  const normalized = normalizeOneRMPayload(payload);
  const entries = normalized?.entries ?? [];

  let bestDirect1RMKg: number | null = null;
  let bestEstimated1RMKg: number | null = null;
  let bestOverallKg: number | null = null;
  let bestGroupLabel: string | null = null;
  let directCount = 0;
  let estimatedCount = 0;
  let bothMethodsCount = 0;

  entries.forEach((entry) => {
    const direct = entry.direct1RMKg;
    const estimated = entry.estimated1RMKg;
    const overall = direct ?? estimated;

    if (direct != null) {
      directCount += 1;
      if (bestDirect1RMKg == null || direct > bestDirect1RMKg) {
        bestDirect1RMKg = direct;
      }
    }

    if (estimated != null) {
      estimatedCount += 1;
      if (bestEstimated1RMKg == null || estimated > bestEstimated1RMKg) {
        bestEstimated1RMKg = estimated;
      }
    }

    if (direct != null && estimated != null) {
      bothMethodsCount += 1;
    }

    if (overall != null && (bestOverallKg == null || overall > bestOverallKg)) {
      bestOverallKg = overall;
      bestGroupLabel = entry.muscleGroup;
    }
  });

  return {
    groupCount: entries.length,
    directCount,
    estimatedCount,
    bothMethodsCount,
    bestDirect1RMKg,
    bestEstimated1RMKg,
    bestOverallKg,
    bestGroupLabel,
  };
};

export const buildOneRMPayload = (input: {
  entries: Array<Partial<OneRMEntry>>;
  notes?: string;
  patientContext?: AssessmentPatientContext;
  professionalContext?: AssessmentProfessionalContext;
  finishedAt?: string;
}): OneRMResultPayload | null => {
  const entries = input.entries.map(normalizeOneRMEntry).filter((entry): entry is OneRMEntry => entry != null);
  if (!entries.length) return null;

  return {
    entries,
    notes: sanitizeText(input.notes) || undefined,
    patientContext: input.patientContext,
    professionalContext: input.professionalContext,
    estimationMethod: 'mean_mayhew_wathen',
    finishedAt: sanitizeText(input.finishedAt) || undefined,
    protocolSteps: ONE_RM_PROTOCOL_STEPS.slice(),
    referenceNotes: ONE_RM_REFERENCE_NOTES.slice(),
  };
};

export const formatOneRMKg = (value: number | null | undefined, decimals = 1): string =>
  typeof value === 'number' && Number.isFinite(value) ? `${value.toFixed(decimals)} kg` : '—';
